import { fetchGigyaJWTPromise } from 'api/gigya';
import { FAVORITE_ALL_PODCASTS_SLUG, FAVORITE_ALL_STATIONS_SLUG, FAVORITE_ALL_CLIPS_SLUG } from 'globalConst/const';
import CONTENT_TYPES from 'globalConst/contentTypes-const';
import { queryRequest } from './client';
import { isEmpty, logger } from 'util/index';

import {
  getUser,
  getFavoritePodcasts,
  getFavoriteStations,
  getFavorites,
  setFavorite,
  createProgress,
  getPodcastProgress,
  getShowProgress,
} from './queries';
import { PodcastCollection, StationCollection } from '../models';

/**
 * Fetch the User object from the Universal API
 * @param {string} userToken
 * @returns {Promise.<object>}
 */
export const fetchUser = (userToken) =>
  new Promise((resolve, reject) => {
    queryRequest({ token: userToken, query: getUser, usePost: true })
      .then((resp) => {
        const {
          data: { user },
        } = resp;

        if (!user) throw new Error('Malformed response. Expected a User object in the response.', resp);

        resolve(user);
      })
      .catch((err) => {
        logger.error(err);
        reject();
      });
  });

/**
 * Fetch a favorited podcasts from the universal api
 * @param {string} slug
 * @returns {Object} Podcasts
 */

export const fetchFavoritePodcasts = async (userToken, slug) => {
  const { data } =
    (await queryRequest({
      query: getFavoritePodcasts,
      variables: { slug },
      token: userToken,
      usePost: true,
    })) || {};
  const favoritePodcasts = new PodcastCollection(data.collection);
  return favoritePodcasts.items && favoritePodcasts.items.filter((item) => item.userPreference.favourite);
};

/**
 * Fetch a favorited stations from the universal api
 * @param {string} slug
 * @returns {Object} Stations
 */
export const fetchFavoriteStations = async (userToken, slug) => {
  const { data } =
    (await queryRequest({
      query: getFavoriteStations,
      variables: { slug },
      token: userToken,
      usePost: true,
    })) || {};
  const favoriteStations = new StationCollection(data.collection);
  return favoriteStations.items && favoriteStations.items.filter((item) => item.userPreference.favourite);
};

function filterFavorites(favorites) {
  const { items } = favorites || {};
  if (isEmpty(items)) return [];
  return items.filter((item) => item.userPreference.favourite).map(({ slug, id }) => ({ slug, id }));
}
export const fetchFavorites = async (userToken) => {
  const { data } =
    (await queryRequest({
      query: getFavorites,
      variables: {
        stationsSlug: FAVORITE_ALL_STATIONS_SLUG,
        podcastsSlug: FAVORITE_ALL_PODCASTS_SLUG,
        clipsSlug: FAVORITE_ALL_CLIPS_SLUG,
      },
      token: userToken,
      usePost: true,
    })) || {};
  return {
    podcasts: filterFavorites(data.podcasts),
    stations: filterFavorites(data.stations),
    clips: filterFavorites(data.clips),
  };
};

/**
 * Fetch progress for a podcast or show from the universal api
 * @param {string} slug
 * @returns {Object} Progress Map
 */
export const fetchProgress = async ({ slug, contentTypeId }) => {
  const token = await fetchGigyaJWTPromise();

  let query;
  if (contentTypeId === CONTENT_TYPES.PODCAST) {
    query = getPodcastProgress;
  } else if (contentTypeId === CONTENT_TYPES.SHOW) {
    query = getShowProgress;
  }

  if (!query) throw new Error(`fetchProgress not supported for ${contentTypeId}`);

  const { data } =
    (await queryRequest({
      query,
      variables: { slug },
      token,
      usePost: true,
    })) || {};
  return (
    data && data.item.episodes && data.item.episodes.reduce((acc, { id, progress }) => ({ ...acc, [id]: progress }), {})
  );
};

/**
 * Mutating the userPreference object in the Universal API
 * @param {string} userToken
 * @param {string} contentId
 * @param {string} contentType
 * @param {boolean} favourite
 * @returns {Promise.<object>}
 */
export const mutateFavorite = (userToken, contentId, contentType, favourite) =>
  new Promise((resolve, reject) => {
    const userPreference = { contentId, contentType, favourite };
    queryRequest({
      query: setFavorite,
      variables: { userPreference },
      token: userToken,
      usePost: true,
    })
      .then((resp) => resolve(resp))
      .catch((err) => {
        logger.error(err);
        reject();
      });
  });

/**
 * Mutating the clipProgress object in the Universal API
 * @param {string} userToken
 * @param {string} contentId
 * @param {string} contentType
 * @param {string} eventType
 * @param {number} durationLength
 * @returns {Promise.<object>}
 */
export const mutateProgress = (userToken, contentId, eventType, durationLength) =>
  new Promise((resolve, reject) => {
    const inputProgress = {
      contentId,
      contentType: 'clip',
      eventType,
      durationLength,
    };

    queryRequest({
      query: createProgress,
      variables: { inputProgress },
      token: userToken,
      usePost: true,
    })
      .then((resp) => resolve(resp))
      .catch((err) => {
        logger.error(err);
        reject();
      });
  });
